import React, { useMemo, useState } from 'react';
import { coter } from '../moteur/index.js';
import { memeConfig, extraireConfig, PROFIL_VIERGE } from '../profils.js';
import { reponseClient, lisible } from '../format.js';
import Segmente from '../composants/Segmente.jsx';
import BlocParametres from '../composants/BlocParametres.jsx';
import CarteResultat from '../composants/CarteResultat.jsx';
import CarteMarge from '../composants/CarteMarge.jsx';
import BoutonCopier from '../composants/BoutonCopier.jsx';
import ActionsProfil from '../composants/ActionsProfil.jsx';
import Info from '../composants/Info.jsx';

/**
 * Écran Coter - document 04, écran 1.
 *
 * Le recalcul est fait à chaque frappe, sans debounce : le moteur est une
 * fonction pure de quelques dizaines d'opérations, très en dessous des 16 ms
 * exigées. Différer le calcul ajouterait une latence perceptible pour rien.
 *
 * Aucune règle de calcul ici (AD-3) : tout chiffre affiché vient de `coter`.
 */
export default function Coter({ profils, rejeu, onEnregistrer, onEnregistrerProfil, onConfirmer }) {
  const [profilId, setProfilId] = useState(
    () => (profils.find((p) => p.isDefault) ?? profils[0])?.id
  );
  // Le profil sélectionné peut disparaître si l'utilisateur le supprime depuis
  // Réglages : on retombe sur le défaut plutôt que sur un écran vide.
  const profil = profils.find((p) => p.id === profilId) ?? profils.find((p) => p.isDefault) ?? profils[0];

  const [direction, setDirection] = useState('out');
  const [basis, setBasis] = useState('envoi');
  const [montant, setMontant] = useState('');
  const [note, setNote] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [enregistre, setEnregistre] = useState(false);
  const [confirmation, setConfirmation] = useState(null);   // null | 'choix' | 'fait'

  // Les paramètres sont initialisés depuis le profil puis ajustables sans le
  // modifier : une cotation ponctuelle ne doit pas réécrire un profil
  // réutilisable.
  const [params, setParams] = useState(() => depuisProfil(profil));

  // Rejeu d'un devis - document 04 : « recharge ces entrées dans l'écran Coter
  // pour repartir d'une base (crée un nouveau devis si enregistré, ne modifie
  // pas l'original) ». Le profil rechargé est le SNAPSHOT du devis, pas le
  // profil courant : celui-ci a pu changer depuis.
  const [rejeuApplique, setRejeuApplique] = useState(null);
  if (rejeu && rejeu.id !== rejeuApplique) {
    setRejeuApplique(rejeu.id);
    setDirection(rejeu.direction);
    setBasis(rejeu.basis);
    setMontant(String(rejeu.inputAmount));
    setNote(rejeu.note ?? '');
    setParams({
      officialRate: rejeu.profileSnapshot.officialRate,
      rateOut: rejeu.profileSnapshot.rateOut,
      rateIn: rejeu.profileSnapshot.rateIn,
      serviceFixed: rejeu.profileSnapshot.serviceFixed,
      servicePercent: rejeu.profileSnapshot.servicePercent,
      operatorPercent: rejeu.profileSnapshot.operatorPercent,
      operatorFixed: rejeu.profileSnapshot.operatorFixed,
      operatorCap: rejeu.profileSnapshot.operatorCap,
      feeMode: rejeu.feeMode,
    });
    if (rejeu.profileId && profils.some((p) => p.id === rejeu.profileId)) setProfilId(rejeu.profileId);
  }

  // Le profil peut être modifié depuis Réglages pendant que Coter est monté :
  // sans cette resynchronisation, l'écran continuerait de coter sur des valeurs
  // que l'utilisateur croit avoir changées.
  const signature = profil ? `${profil.id}:${profil.updatedAt}` : '';
  const [vue, setVue] = useState(signature);
  if (vue !== signature) {
    setVue(signature);
    // Sauf juste après un rejeu : les paramètres viennent alors du snapshot du
    // devis, les écraser par le profil courant annulerait le rejeu.
    if (!rejeu || rejeu.id === rejeuApplique) setParams(depuisProfil(profil));
  }

  const changerProfil = (id) => {
    setProfilId(id);
    setParams(depuisProfil(profils.find((p) => p.id === id)));
  };

  const majParams = (partiel) => setParams((p) => ({ ...p, ...partiel }));

  const saisi = Number.parseFloat(String(montant).replace(',', '.'));
  const valide = Number.isFinite(saisi) && saisi > 0;

  const resultat = useMemo(
    () => coter({ direction, basis, feeMode: params.feeMode, montant: valide ? saisi : 0, profil: params }),
    [direction, basis, params, saisi, valide]
  );

  const devise = profil.cfaCurrency;
  // La devise du profil désigne le corridor dans le cas courant ; il reste
  // modifiable, un même profil pouvant servir des deux côtés.
  const [corridorChoisi, setCorridor] = useState(null);
  const corridor = corridorChoisi ?? (devise === 'XOF' ? 'XOF' : 'XAF');
  const uniteSaisie = uniteDuChamp(direction, basis, devise);
  const relecture = valide ? lisible(saisi, uniteSaisie) : null;

  // Les paramètres à l'écran s'écartent-ils du profil chargé ? La question est
  // posée au domaine : c'est lui qui sait quels champs participent au calcul.
  const modifie = !memeConfig(params, profil);

  const enregistrerProfil = async (donnees) => {
    const enregistre = await onEnregistrerProfil(donnees);
    // Le nouveau profil devient le profil courant : l'avoir créé sans le
    // sélectionner laisserait l'écran marqué « modifié » juste après un
    // enregistrement réussi.
    if (enregistre?.id) setProfilId(enregistre.id);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--espace-4)' }}>
      <label style={{ display: 'block' }}>
        <span className="etiquette">Profil</span>
        <select
          value={profilId}
          onChange={(e) => changerProfil(e.target.value)}
          className="champ"
          style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}
        >
          {profils.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>

      <div className="grille-2">
        <Segmente
          etiquette="Sens"
          valeur={direction}
          onChange={setDirection}
          options={[
            { valeur: 'out', libelle: `EUR → ${devise}` },
            { valeur: 'in', libelle: `${devise} → EUR` },
          ]}
        />
        <Segmente
          etiquette="Base de saisie"
          aide={
            <Info titre="Base de saisie">
              De quel côté vous partez. « Je fixe l’envoi » : vous saisissez ce
              que le client donne, l’application calcule ce qui arrive.
              « Je fixe la réception » : vous saisissez ce qui doit arriver,
              elle calcule ce qu’il faut envoyer.
            </Info>
          }
          valeur={basis}
          onChange={setBasis}
          options={[
            { valeur: 'envoi', libelle: 'Je fixe l’envoi' },
            { valeur: 'reception', libelle: 'Je fixe la réception' },
          ]}
        />
      </div>

      <label style={{ display: 'block' }}>
        <span className="etiquette">Montant ({uniteSaisie})</span>
        <input
          type="text"
          inputMode="decimal"
          autoFocus
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
          placeholder="0"
          className="champ"
          style={{ marginTop: 'var(--espace-1)', fontSize: '1.75rem', padding: 'var(--espace-3)' }}
        />
        {/* Relecture du montant : c'est le chiffre le plus long de l'écran, et
            le seul saisi à la main sans séparateur. */}
        {relecture && (
          <span className="chiffre" aria-hidden="true" style={{
            display: 'block', marginTop: 'var(--espace-1)',
            fontSize: '0.85rem', color: 'var(--couleur-encre-douce)',
          }}>
            {relecture}
          </span>
        )}
      </label>

      <BlocParametres
        ouvert={ouvert}
        onToggle={() => setOuvert((o) => !o)}
        p={params}
        onChange={majParams}
        devise={devise}
      >
        <ActionsProfil
          profil={profil}
          modifie={modifie}
          onMettreAJour={() => enregistrerProfil({ ...profil, ...extraireConfig(params) })}
          onCreer={(nom) => enregistrerProfil({
            ...PROFIL_VIERGE,
            ...extraireConfig(params),
            cfaCurrency: profil.cfaCurrency,
            name: nom,
            order: profils.length,
          })}
          onRetablir={() => setParams(depuisProfil(profil))}
        />
      </BlocParametres>

      {/* Montant vide ou nul : résultat neutre, jamais d'erreur agressive. */}
      {valide ? (
        <>
          <CarteResultat r={resultat} devise={devise} basis={basis} />
          <CarteMarge r={resultat} officialRate={params.officialRate} />

          <label style={{ display: 'block' }}>
            <span className="etiquette">Note de contexte</span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Marcel - dot"
              className="champ"
              style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}
            />
          </label>

          <div style={{ display: 'flex', gap: 'var(--espace-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <BoutonCopier texte={reponseClient(resultat, devise)} />
            <button
              type="button"
              onClick={async () => {
                await onEnregistrer({
                  profil, params, direction, basis, feeMode: params.feeMode,
                  inputAmount: saisi, note, result: resultat,
                });
                setEnregistre(true);
                setTimeout(() => setEnregistre(false), 2000);
              }}
              style={{
                padding: 'var(--espace-2) var(--espace-4)',
                border: '1px solid var(--couleur-trait-fort)',
                borderRadius: 'var(--rayon)',
                background: enregistre ? 'var(--couleur-succes)' : 'transparent',
                color: enregistre ? 'var(--couleur-sur-accent)' : 'var(--couleur-encre)',
                fontWeight: 600,
              }}
            >
              {enregistre ? 'Devis enregistré' : 'Enregistrer le devis'}
            </button>

            <button
              type="button"
              onClick={() => setConfirmation(confirmation ? null : 'choix')}
              aria-expanded={confirmation === 'choix'}
              style={{
                padding: 'var(--espace-2) var(--espace-4)',
                border: '1px solid var(--couleur-accent)',
                borderRadius: 'var(--rayon)',
                background: confirmation === 'fait' ? 'var(--couleur-accent)' : 'transparent',
                color: confirmation === 'fait' ? 'var(--couleur-sur-accent)' : 'var(--couleur-accent)',
                fontWeight: 600,
              }}
            >
              {confirmation === 'fait' ? 'Mouvement enregistré' : 'Confirmer l’opération'}
            </button>
          </div>

          {/* Confirmer n'est pas enregistrer. Un devis est un prix proposé ;
              confirmer déclare que l'opération s'est faite, et c'est la seule
              chose qui touche le livre. Le corridor est demandé ici parce que
              le profil dit la devise, pas la zone où l'argent a circulé. */}
          {confirmation === 'choix' && (
            <div className="carte" style={{ display: 'grid', gap: 'var(--espace-3)' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
                Confirmer enregistre le devis <strong>et</strong> l’inscrit au livre :
                {direction === 'out'
                  ? <> un <strong>achat d’euros</strong>, votre caisse CFA baisse et votre stock monte.</>
                  : <> une <strong>vente d’euros</strong>, votre caisse CFA monte et votre stock baisse.</>}
                {' '}Le devis passe au statut « réglé ».
              </p>
              <Segmente
                etiquette="Corridor de l’opération"
                valeur={corridor}
                onChange={setCorridor}
                options={[
                  { valeur: 'XAF', libelle: 'XAF · CEMAC' },
                  { valeur: 'XOF', libelle: 'XOF · UEMOA' },
                ]}
              />
              <div style={{ display: 'flex', gap: 'var(--espace-2)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={async () => {
                    await onConfirmer({
                      profil, params, direction, basis, feeMode: params.feeMode,
                      inputAmount: saisi, note, result: resultat,
                    }, corridor);
                    setConfirmation('fait');
                    setTimeout(() => setConfirmation(null), 2500);
                  }}
                  style={{
                    padding: 'var(--espace-2) var(--espace-4)',
                    border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
                    background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
                  }}
                >
                  Confirmer en {corridor}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmation(null)}
                  style={{
                    padding: 'var(--espace-2) var(--espace-4)',
                    border: '1px solid var(--couleur-trait-fort)', borderRadius: 'var(--rayon)',
                    background: 'transparent', color: 'var(--couleur-encre)',
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p style={{ color: 'var(--couleur-encre-douce)', margin: 0 }}>
          Saisissez un montant pour obtenir une cotation.
        </p>
      )}
    </div>
  );
}

function depuisProfil(p) {
  return {
    officialRate: p.officialRate,
    rateOut: p.rateOut,
    rateIn: p.rateIn,
    serviceFixed: p.serviceFixed,
    servicePercent: p.servicePercent,
    operatorPercent: p.operatorPercent,
    operatorFixed: p.operatorFixed,
    operatorCap: p.operatorCap,
    feeMode: p.feeMode,
  };
}

/** L'unité du champ se déduit du couple sens + base, jamais du seul sens. */
function uniteDuChamp(direction, basis, devise) {
  if (direction === 'out') return basis === 'envoi' ? 'EUR' : devise;
  return basis === 'envoi' ? devise : 'EUR';
}
